import { makeAutoObservable, runInAction } from 'mobx';
import { BACKEND_MODULE_TYPES, TWO_FACTOR_AUTH_TYPES } from 'components/common/constants';
import { setConfirmationActionType } from 'services/authUtils';
import {
  loginUser,
  loginPin,
  changePasswordUser,
  resendSecurityCode,
  forceChangeConfirmationTypeRequest
} from 'services/requestAgent';

class AuthStore {
  isLoading = false;
  isLoginSuccess = false;
  isUserAlreadyRegistered = false;
  isPinSuccess = false;
  isPasswordChanged = false;
  isShowSwitchConfirmationMethodScreen = false;
  error = null;
  confirmationActionType = null;
  confirmationId = null;
  isGoogle2FAEnabled = false;
  phone = null;
  availableConfirmationTypes = [];
  confirmationExpiresAt = null;
  scope = null;
  isPushNotificationEnabled = false;

  values = {
    // Left blank on purpose — react-phone-input-2 shows the default dial code as a UI prefix via
    // its own `country` prop (see Input.js), so pre-filling this would show as an actually-typed
    // number in the field.
    phone: '',
    username: '',
    password: '',
    code: ''
  };

  constructor() {
    makeAutoObservable(this);
  }

  setIsLoading(status) {
    this.isLoading = status;
    this.error = null;
  }

  // TRN-7473: login form state of an earlier attempt — a fresh page load always started without it
  resetLoginForm() {
    this.error = null;
    this.values.phone = '';
    this.values.username = '';
    this.values.password = '';
    this.values.code = '';
  }

  setIsLoginSuccess(status) {
    this.isLoginSuccess = status;
  }

  setIsPinSuccess(status) {
    this.isPinSuccess = status;
  }

  setIsUserAlreadyRegistered(status) {
    this.isUserAlreadyRegistered = status;
  }

  setIsShowSwitchConfirmationMethodScreen(status) {
    this.isShowSwitchConfirmationMethodScreen = status;
  }

  setUsername(username) {
    this.values.username = username;
  }

  setPhone(phone) {
    this.values.phone = phone;
  }

  setPassword(password) {
    this.values.password = password;
  }

  setCode(code) {
    this.values.code = code;
  }

  setConfirmationId(id) {
    this.confirmationId = id;
  }

  setUserAuthorizationData(confirmationData) {
    const { confirmationId, phone, confirmationType, availableConfirmationTypes, expiresAt, scope } = confirmationData;

    this.isUserAlreadyRegistered = true;
    this.isLoginSuccess = true;
    this.confirmationId = confirmationId;
    this.phone = phone;
    this.confirmationActionType = confirmationType;
    this.isGoogle2FAEnabled = confirmationType === TWO_FACTOR_AUTH_TYPES.GOOGLE_AUTH;
    this.isPushNotificationEnabled = confirmationType === TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION;
    this.availableConfirmationTypes = availableConfirmationTypes || [];
    this.confirmationExpiresAt = expiresAt;
    this.scope = scope;
  }

  reset() {
    this.isLoading = false;
    this.isLoginSuccess = false;
    this.isUserAlreadyRegistered = false;
    this.isPinSuccess = false;
    this.isShowSwitchConfirmationMethodScreen = false;
    this.confirmationActionType = null;
    this.confirmationId = null;
    this.isGoogle2FAEnabled = false;
    this.isPushNotificationEnabled = false;
    this.availableConfirmationTypes = [];
    this.confirmationExpiresAt = null;
    this.scope = null;
    this.values.username = '';
    this.values.password = '';
    this.values.code = '';
  }

  async resendSecurityCode() {
    try {
      const { expires_at: expiresAt } = await resendSecurityCode(BACKEND_MODULE_TYPES.OAUTH, this.confirmationId);

      runInAction(() => {
        this.error = null;
        if (expiresAt) this.confirmationExpiresAt = expiresAt;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    }
  }

  async sendAuthorizationData() {
    this.setIsLoading(true);

    try {
      const response = await loginUser({
        phone: this.values.phone,
        username: this.values.username || this.values.phone,
        password: this.values.password
      });

      runInAction(() => {
        const { confirmationId, confirmationType, expiresAt, availableConfirmationTypes, scope } = response;
        this.isLoginSuccess = true;
        this.isLoading = false;
        this.confirmationActionType = confirmationType;
        this.confirmationId = confirmationId;
        this.isGoogle2FAEnabled = confirmationType === TWO_FACTOR_AUTH_TYPES.GOOGLE_AUTH;
        this.isPushNotificationEnabled = confirmationType === TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION;
        this.phone = this.values.phone;
        this.availableConfirmationTypes = availableConfirmationTypes || [];
        this.confirmationExpiresAt = expiresAt;
        this.scope = scope;
      });
    } catch (err) {
      runInAction(() => {
        this.isLoading = false;
        this.error = err;
      });
    }
  }

  forceSwitchToAnotherConfirmationType = async (confirmationType) => {
    this.setIsLoading(true);

    try {
      const response = await forceChangeConfirmationTypeRequest(
        this.confirmationId,
        confirmationType,
        BACKEND_MODULE_TYPES.OAUTH
      );

      runInAction(() => {
        const { confirmation_id: confirmationId, confirmation_type: type, expires_at: expiresAt } = response;
        setConfirmationActionType(type);
        this.confirmationActionType = type;
        this.confirmationId = confirmationId;
        this.confirmationExpiresAt = expiresAt;
        this.isPushNotificationEnabled = type === TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION;
        this.isGoogle2FAEnabled = type === TWO_FACTOR_AUTH_TYPES.GOOGLE_AUTH;
        this.error = null;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
        this.isShowSwitchConfirmationMethodScreen = false;
      });
    }
  };

  async sendCode(externalConfirmationId = null) {
    if (this.isLoading) {
      return;
    }

    const confirmationId = externalConfirmationId || this.confirmationId;

    this.setIsLoading(true);
    try {
      const response = await loginPin(this.values.code, confirmationId);
      if (response.success) {
        runInAction(() => {
          this.isPinSuccess = true;
          this.isUserAlreadyRegistered = false;
          this.isLoading = false;
          this.confirmationId = null;
          this.scope = response.scope;
        });
      } else {
        throw response;
      }
    } catch (err) {
      runInAction(() => {
        this.isLoading = false;
        this.error = err;
      });
    }
  }

  async sendPasswordChange() {
    this.setIsLoading(true);
    try {
      await changePasswordUser(this.values.password);
      runInAction(() => {
        this.isPasswordChanged = true;
        this.isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.isLoading = false;
        this.error = err;
      });
    }
  }
}

export default new AuthStore();
